import { formatDate, getSupportContactLink, type SurveyState, type TFunction } from '@psyomics/ui-common';
import { ContentCard, GenericError, Header, IllustrationName, LoadingSpinner, Text } from '@psyomics/ui-components';
import { CircleArrowRight, CircleCheck } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GetServiceUserInvitesQuery, Invite, ReferralSessionStatus, ReferralStatus } from '../generated/invites';
import { useServiceUserInvites } from '../hooks/use-service-user-invites';

const images: IllustrationName[] = ['WomanPuzzle', 'BrainHealth', 'HeartInHands'] as const;

const TWO = 2;
const randomImages = [...images].sort(() => Math.random() * TWO - 1);

interface GetSurveyStateProps {
  hasAnswers: boolean;
  isCompleted: boolean;
  referralCancelled: boolean;
  referralSessionExpired: boolean;
}

const getSurveyState = ({
  hasAnswers,
  isCompleted,
  referralCancelled,
  referralSessionExpired,
}: GetSurveyStateProps): SurveyState => {
  if (referralSessionExpired) return 'expired';
  if (referralCancelled) return 'cancelled';
  if (isCompleted) return 'completed';
  if (hasAnswers) return 'inProgress';
  return 'notStarted';
};

const getStateIcon = (state: SurveyState) => {
  if (state === 'completed') return <CircleCheck size={18} />;
  if (state === 'notStarted' || state === 'inProgress') return <CircleArrowRight size={18} />;

  return undefined;
};

const getCheckInContentCardProps = (
  invite: GetServiceUserInvitesQuery['getServiceUserInvites']['invites'][number],
  t: TFunction
) => {
  const { description: title } = invite.referralSession.referral.service;
  const { contact } = invite.referralSession.referral;
  const { supportContact, supportHref } = getSupportContactLink(contact);
  const referralCancelled = invite.referralSession.referral.status === ReferralStatus.Cancelled;
  const referralSessionExpired = invite.referralSession.status === ReferralSessionStatus.Expired;
  const isCompleted = Boolean(invite.surveyResponse?.completedAt);
  const isDisabled = referralCancelled || referralSessionExpired || (isCompleted && !invite.reviewEnabled);

  const state = getSurveyState({
    hasAnswers: Boolean(invite.surveyResponse?.answers),
    isCompleted,
    referralCancelled,
    referralSessionExpired,
  });

  const stateIcon = getStateIcon(state);

  const stateMessage = t(`page.home.cardMessage.${state}`);
  const sentMessage = t('page.home.cardMessage.sent');
  const sentDate = formatDate(new Date(invite.createdAt));
  const description = `${stateMessage}, ${sentMessage} ${sentDate}`;

  const supportInformation = (state === 'cancelled' || state === 'expired') && (
    <Trans
      components={{
        supportLink: (
          <a className="underline" href={supportHref}>
            {supportContact}
          </a>
        ),
      }}
      i18nKey="page.home.cardMessage.contactSupport"
    />
  );

  return { description, isDisabled, stateIcon, supportInformation, title };
};

function sortInvites(invitations: Invite[]) {
  return invitations.sort((a, b) => Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)));
}

const CheckInContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fetching, data } = useServiceUserInvites();

  const onClick = (invite: Invite) => {
    navigate(
      `/questionnaire/${invite.publicId}${invite.reviewEnabled && invite.status === 'completed' ? '/review' : ''}`
    );
  };

  if (fetching) return <LoadingSpinner />;

  const invites = data?.getServiceUserInvites.invites;
  if (!invites?.length)
    return (
      <GenericError
        ellipse1Variant="indigoPurple"
        illustration="WomanEmptyBox"
        subtitleKey="page.home.noActiveReferral.subtitle"
        titleKey="page.home.noActiveReferral.title"
      />
    );

  const sortedInvites = sortInvites(invites);

  return (
    <div className="flex flex-col gap-3 mt-4">
      <Header level="h1">{t('page.home.checkIns.header')}</Header>
      <Text size="md">{t('page.home.checkIns.info')}</Text>
      {sortedInvites.map((invite, idx) => {
        const { description, isDisabled, stateIcon, supportInformation, title } = getCheckInContentCardProps(invite, t);

        return (
          <div key={invite.publicId}>
            <ContentCard
              description={description}
              disabled={isDisabled}
              image={randomImages[idx % randomImages.length]}
              onClick={() => onClick(invite)}
              stateIcon={stateIcon}
              supportInformation={supportInformation}
              title={title}
            />
          </div>
        );
      })}
    </div>
  );
};

export { CheckInContent, getSupportContactLink, getSurveyState };
